import React, { useEffect, useRef, useState } from "react";
import { Checkbox, Form, Input, Modal, Select, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PhoneInput from "react-phone-input-2";
import { save } from "../../../features/save/saveSlice";
import { addSpaces } from "../../../utils";
import MessageTemplate from "../../mesage-template/MessageTemplate";
import "./mention.css";
import { fetchDistinctSenderNames } from "../../../features/filter/filterSlice";
import { sendSingleWhatsApp } from "../../../features/whatsapp/whatsappSlice";
import { extractErrorMessage, normalizeRecipient } from "../../whatsapp/whatsappUtils";
import WhatsAppSenderSelect from "../../../components/WhatsAppSenderSelect";

const { TextArea } = Input;
const CHANNEL_OPTIONS = [
  { label: "SMS", value: "SMS" },
  { label: "WhatsApp", value: "WHATSAPP" },
];

const SmsIndividualModal = ({ isModalOpen, setIsModalOpen, rowId }) => {
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const { saving } = useSelector((state) => state.save);
  const { saving: whatsappSaving } = useSelector((state) => state.whatsapp);
  const { senderNamesData } = useSelector((state) => state.filterSms);
  const [channels, setChannels] = useState(["SMS"]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState();
  const [phoneData, setPhoneData] = useState({});
  const smsSelected = channels.includes("SMS");
  const whatsappSelected = channels.includes("WHATSAPP");
  const isSubmitting = saving || whatsappSaving;
  const submitLabel = smsSelected && whatsappSelected
    ? "Send SMS + WhatsApp"
    : whatsappSelected
      ? "Send WhatsApp"
      : "Send SMS";

  const handleNumberChange = (e) => {
    setPhoneData((prevData) => ({
      ...prevData,
      mobile: String(e),
    }));
  };

  const onFinish = async (values) => {
    if (!smsSelected && !whatsappSelected) {
      toast.error("Please select at least one channel");
      return;
    }

    const mobile = rowId?.length > 0 ? rowId?.[0]?.chTelephone : phoneData?.mobile || values?.mobile;
    let smsSuccess = !smsSelected;
    let whatsappSuccess = !whatsappSelected;
    let smsError = "";
    let whatsappError = "";
    let smsMessage = "";
    let whatsappMessage = "";

    if (smsSelected) {
      const res = await dispatch(
        save({
          url: "api/v2/sms/single-sms",
          message: values?.message,
          mobile,
          senderId: formData?.senderId,
        })
      );
      smsSuccess = Boolean(res?.payload?.success);
      smsMessage = res?.payload?.messages?.message || "SMS sent successfully";
      smsError = res?.payload?.messages?.message || "SMS send failed";
    }

    if (whatsappSelected) {
      const result = await dispatch(
        sendSingleWhatsApp({
          recipient: normalizeRecipient(mobile),
          messageType: "TEXT",
          text: values?.message,
          senderRegistrationId: values?.senderRegistrationId || formData?.senderRegistrationId || undefined,
        })
      );
      whatsappSuccess = sendSingleWhatsApp.fulfilled.match(result);
      whatsappMessage = result?.payload?.messages?.message || "WhatsApp message queued successfully";
      whatsappError = extractErrorMessage(result?.payload);
    }

    if (smsSuccess && whatsappSuccess) {
      const successMessage = smsSelected && whatsappSelected
        ? "SMS sent and WhatsApp queued successfully"
        : smsSelected
          ? smsMessage
          : whatsappMessage;
      toast.success(successMessage);
      form.resetFields();
      setFormData(undefined);
      setPhoneData({});
      setChannels(["SMS"]);
      setIsModalOpen(false);
      navigate(whatsappSelected && !smsSelected ? "/whatsapp-queue" : "/sent-sms");
      return;
    }

    if (smsSelected && smsSuccess) {
      toast.success(smsMessage);
    } else if (smsSelected) {
      toast.error(smsError);
    }

    if (whatsappSelected && whatsappSuccess) {
      toast.success(whatsappMessage);
    } else if (whatsappSelected) {
      toast.error(whatsappError);
    }

    if (smsSelected && smsSuccess && whatsappSelected && !whatsappSuccess) {
      setChannels(["WHATSAPP"]);
      return;
    }

    if (whatsappSelected && whatsappSuccess && smsSelected && !smsSuccess) {
      setChannels(["SMS"]);
    }
  };
 
  async function handleSelectChange(value, formName) { 
    await setFormData((prevData) => ({
       ...prevData,
       [formName]: value,
     })); 
   }

   function fetchDistinctSenderNamesData() {
    dispatch(fetchDistinctSenderNames());
  }

  useEffect(()=>{
    fetchDistinctSenderNamesData()
  },[])
  
  return (
    <>
      <Modal
        className=""
        title="Message"
        open={isModalOpen}
        onOk={onFinish}
        onCancel={handleCancel}
        width={900}
      >
        <Form
          layout="vertical"
          ref={formRef}
          name="control-ref"
          onFinish={onFinish}
          className="px-[15px]"
          style={{
            maxWidth: "100%",
          }}
          form={form}
        >
          <Form.Item label="Send via">
            <Checkbox.Group
              options={CHANNEL_OPTIONS}
              value={channels}
              onChange={(values) => setChannels(values)}
            />
          </Form.Item>

          {smsSelected && (
            <Form.Item
              name="senderId"
              className=""
              label={
                <span>
                  Select sender name <span className="text-[#FF0000]">*</span>
                </span>
              }
              rules={[
                {
                  required: true,
                  message: "Please select sender name",
                },
              ]}
            >
              <Select
                className=""
                allowClear
                style={{
                  width: "100%",
                }}
                onChange={(value) => {
                  handleSelectChange(value, "senderId");
                }}
                options={
                  senderNamesData?.length > 0 &&
                  senderNamesData?.map((item) => ({
                    value: item,
                    label: item,
                  }))
                }
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label?.toLocaleLowerCase() ?? "").includes(
                    input?.toLocaleLowerCase()
                  )
                }
                onDropdownVisibleChange={() => {
                  fetchDistinctSenderNamesData();
                }}
              />
            </Form.Item>
          )}

          <WhatsAppSenderSelect
            visible={whatsappSelected}
            onChange={(value) => {
              handleSelectChange(value, "senderRegistrationId");
            }}
          />

          {rowId?.length > 0 ? (
            <Form.Item
              label={
                <span>
                  Phone No.<span className="text-[#FF0000]">*</span>
                </span>
              }
              rules={[
                {
                  required: true,
                  message: "Please add office phone no",
                },
              ]}
            >
              <Input
                readOnly={true}
                name="mobile"
                value={addSpaces(rowId?.[0]?.chTelephone)}
                className="input rounded-[6px] border !border-[#cacaca] !h-[42px]"
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="mobile"
              label={
                <span>
                  Phone No.<span className="text-[#FF0000]">*</span>
                </span>
              }
              rules={[
                {
                  required: true,
                  message: "Please add office phone no",
                },
              ]}
            >
              <PhoneInput
                onChange={handleNumberChange}
                country="ke"
                onlyCountries={["ke"]}
                countryCodeEditable={false}
                className="input rounded-[6px] border !border-[#cacaca] !h-[42px]"   
              />
            </Form.Item>
          )}

          <Form.Item
            name="message"
            label={
              <span>
                Custom Message<span className="text-[#FF0000]">*</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: "Please add a comment",
              },
            ]}
          >
            <TextArea
              placeholder="Please add a comment"
              rows={4}
              className="input-textarea"
            />
          </Form.Item>

          {/* <div className="mention-container">
            <Form.Item
              name="message 2"
              label={
                <span>
                  Custom Message <span className="text-[#FF0000]">*</span>
                </span>
              }
              rules={[
                {
                  required: true,
                  message: "Please add a comment",
                },
              ]}
            >
              <TextArea
                placeholder="Please add a comment"
                rows={4}
                className="input-textarea"
                ref={inputRef}
                value={inputValue}
                onChange={handleInputChange}
              />
            </Form.Item>
    

            {dropdownVisible && (
              <div className="dropdown">
                {dropdownItems.map((item) => (
                  <div
                    key={item}
                    className="dropdown-item"
                    onClick={() => handleDropdownClick(item)}
                  >
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div> */}

         

          <div className="flex justify-between mt-[48px] ">
            <div className="justify-start"></div>
            <div className="justify-end flex items-center mb-[58px] gap-x-5">
              <div className="w-[150px] ">
                <button
                  key="back"
                  type="button"
                  onClick={handleCancel}
                  className="cstm-btn !bg-white !text-[#388E3C] !border !border-[#388E3C]"
                >
                  Cancel
                </button>
              </div>

              <div className="w-[150px]">
                <button
                  key="submit"
                  type="submit"
                  className="cstm-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Spin /> : submitLabel}
                </button>
              </div>
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};
export default SmsIndividualModal;
